import { RefObject, useEffect } from 'react';

/** Below the sticky header (z-30) for any realistic depth, above pinned body cells (z-[1]). */
const STICKY_Z_BASE = 20;

export interface StickyRowBox {
  level: number;
  height: number;
  /** Bottom of the row's last descendant, relative to the scroll area; `null` while no children are rendered. */
  subtreeBottom: number | null;
}

/**
 * Sticky `top` of each expanded row, in DOM order (`null` — the row does not stick).
 *
 * A row stacks under its stuck ancestor (or the header), but never past the end of its own subtree: once the last
 * descendant scrolls by, the row is pushed out with it instead of hanging over the next group.
 */
export function layoutStickyRows(rows: StickyRowBox[], headerHeight: number): (number | null)[] {
  const ancestors: { level: number; bottom: number }[] = [];

  return rows.map(({ level, height, subtreeBottom }) => {
    if (subtreeBottom === null) return null;

    while (ancestors.length && ancestors[ancestors.length - 1].level >= level) ancestors.pop();
    const parent = ancestors[ancestors.length - 1];

    const top = Math.min(parent ? parent.bottom : headerHeight, subtreeBottom - height);
    ancestors.push({ level, bottom: top + height });
    return top;
  });
}

const levelOf = (row: HTMLTableRowElement) => Number(row.dataset.rowLevel);

/** Geometry of every expanded row; a subtree runs until the next row of the same or a shallower level. */
function measureStickyRows(area: HTMLElement) {
  const rows = Array.from(area.querySelectorAll<HTMLTableRowElement>('tbody > tr[data-row-level]'));
  const areaTop = area.getBoundingClientRect().top;

  return rows.flatMap((row, index) => {
    if (!row.hasAttribute('data-sticky-row')) return [];

    const level = levelOf(row);
    let last = index;
    while (last + 1 < rows.length && levelOf(rows[last + 1]) > level) last++;

    return {
      row,
      level,
      height: row.offsetHeight,
      subtreeBottom: last === index ? null : rows[last].getBoundingClientRect().bottom - areaTop
    };
  });
}

/**
 * Stacks expanded hierarchy rows (`tr[data-sticky-row]`) under the sticky header, so the ancestors of whatever is
 * scrolled into view stay visible, level under level. CSS alone cannot place them — the offset depends on the stuck
 * ancestors and on where the subtree ends — so the hook writes it to each row as `--sticky-top`, plus `--sticky-z`:
 * shallower rows paint above deeper ones (a child pushed out slides under its parent), +1 is left for pinned cells.
 *
 * Recomputed once per frame on scroll, resize and row changes (expand, collapse, lazy load).
 */
export function useStickyRows(ref: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const area = ref.current;
    if (!area) return;

    let frame = 0;

    const update = () => {
      frame = 0;
      // All reads before any write: a stuck row does not move the layout of the others
      const rows = measureStickyRows(area);
      const tops = layoutStickyRows(rows, area.querySelector('thead')?.offsetHeight ?? 0);

      rows.forEach(({ row, level }, index) => {
        const top = tops[index];
        if (top === null) {
          row.style.removeProperty('--sticky-top');
          row.style.removeProperty('--sticky-z');
        } else {
          row.style.setProperty('--sticky-top', `${top}px`);
          row.style.setProperty('--sticky-z', String(STICKY_Z_BASE - 2 * level));
        }
      });
    };

    const scheduleUpdate = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    update();
    area.addEventListener('scroll', scheduleUpdate, { passive: true });
    const resizeObserver = new ResizeObserver(scheduleUpdate);
    resizeObserver.observe(area);
    if (area.firstElementChild) resizeObserver.observe(area.firstElementChild);
    const mutationObserver = new MutationObserver(scheduleUpdate);
    mutationObserver.observe(area, { childList: true, subtree: true, attributeFilter: ['data-sticky-row'] });

    return () => {
      cancelAnimationFrame(frame);
      area.removeEventListener('scroll', scheduleUpdate);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [ref]);
}
